import { creatorbase, creatorbaseQueries } from '@round/api';
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import uniq from 'lodash/uniq';
import moment from 'moment';
import { isNumber } from 'utility/utility';
import { ProjectsTableRow, resolveRowId, Meta } from './components/ProjectsTable/helpers';
import {
    useBrandsQueriesByIds,
    usePartnershipsQueriesByIds,
    useProjectsListInfinite,
    useTeamsQueriesByIds,
    useUsersQueriesByIds,
} from '@round/creatorbase-helpers/api-hooks';
import uniqBy from 'lodash/uniqBy';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';

const timeSeriesOptions = creatorbaseQueries.timeSeries.options;

type ListParams = Omit<creatorbase.GetProjectsParams, 'page' | 'page_size'>;
type Config = { enabled?: boolean };
type TimeSeriesData = creatorbase.TimeSeriesResponse<'tiktok_daily_change' | 'instagram_daily_change'>[number];
type ProjectListMeta = Pick<
    Meta,
    | 'isLoading'
    | 'hasError'
    | 'getIsRowLoading'
    | 'isBrandLoading'
    | 'isTeamLoading'
    | 'isUsersLoading'
    | 'isPartnershipLoading'
    | 'isTimeSeriesLoading'
    | 'isTimeSeriesError'
>;

export function useProjectsList(params: ListParams, config: Config = {}) {
    const today = moment().format('YYYY-MM-DD');
    const twoWeeksAgo = moment().subtract(14, 'days').format('YYYY-MM-DD');

    const projectsQuery = useProjectsListInfinite({ ...params, page_size: 25 }, config);
    const pages = useMemo(() => projectsQuery.data?.pages ?? [], [projectsQuery.data?.pages]);
    const projects = useMemo(() => pages.flatMap((page) => page.results), [pages]);

    const projectPageIndex = useMemo(() => {
        const map = new Map<number, number>();
        pages.forEach((page, i) => page.results.forEach((p) => map.set(p.id, i)));
        return map;
    }, [pages]);

    const brands = useBrandsQueriesByIds(
        pages.map((page) => uniq(page.results.map((p) => p.brand_id)).filter(isNumber)),
        {
            combine: (queries) => ({
                data: uniqBy(
                    queries.flatMap((q) => q.data?.results ?? []),
                    'id'
                ).filter((b): b is creatorbase.Brand => !creatorbase.isPublicBrand(b)),
                isLoadingFor: (row: ProjectsTableRow | {}) => {
                    const id = resolveRowId(row);
                    return typeof id === 'number' ? queries[projectPageIndex.get(id) ?? -1]?.isLoading ?? false : false;
                },
            }),
        }
    );

    const teams = useTeamsQueriesByIds(
        pages.map((page) => uniq(page.results.map((p) => p.team_id)).filter(isNumber)),
        {
            combine: (queries) => ({
                data: uniqBy(
                    queries.flatMap((q) => q.data?.results ?? []),
                    'id'
                ),
                isLoadingFor: (row: ProjectsTableRow | {}) => {
                    const id = resolveRowId(row);
                    return typeof id === 'number' ? queries[projectPageIndex.get(id) ?? -1]?.isLoading ?? false : false;
                },
            }),
        }
    );

    const isProjectSharingEnabled = useFeatureFlags().isEnabled('project_sharing');
    const partnerships = usePartnershipsQueriesByIds(
        pages.map((page) =>
            isProjectSharingEnabled ? uniq(page.results.map((p) => p.owned_by)).filter(isNumber) : []
        ),
        {
            combine: (queries) => ({
                data: uniqBy(
                    queries.flatMap((q) => q.data?.results ?? []),
                    'id'
                ),
                isLoadingFor: (row: ProjectsTableRow | {}) => {
                    const id = resolveRowId(row);
                    return typeof id === 'number' ? queries[projectPageIndex.get(id) ?? -1]?.isLoading ?? false : false;
                },
            }),
        }
    );

    const users = useUsersQueriesByIds(
        pages.map((page) => uniq(page.results.flatMap((p) => p.users_assigned_to_campaigns))),
        {
            combine: (queries) => ({
                data: uniqBy(
                    queries.flatMap((q) => q.data?.results ?? []),
                    'id'
                ),
                isLoadingFor: (row: ProjectsTableRow | {}) => {
                    const id = resolveRowId(row);
                    return typeof id === 'number' ? queries[projectPageIndex.get(id) ?? -1]?.isLoading ?? false : false;
                },
            }),
        }
    );

    const timeSeries = useQueries({
        queries: pages.map((page) => {
            const ids = [...uniq(page.results.map((p) => p.song_id)).filter(isNumber)].sort((a, b) => a - b);
            return {
                ...timeSeriesOptions.list({
                    type: 'song',
                    ids,
                    fields: ['tiktok_daily_change', 'instagram_daily_change'],
                    start_date: twoWeeksAgo,
                    end_date: today,
                }),
                enabled: ids.length > 0,
            };
        }),
        combine: (queries) => {
            const data = new Map<number, TimeSeriesData>();
            queries.forEach((q) => {
                Object.entries(q.data ?? {}).forEach(([id, ts]) => {
                    data.set(Number(id), ts);
                });
            });
            return {
                data,
                isLoadingFor: (row: ProjectsTableRow | {}) => {
                    const id = resolveRowId(row);
                    return typeof id === 'number' ? queries[projectPageIndex.get(id) ?? -1]?.isLoading ?? false : false;
                },
                isErrorFor: (row: ProjectsTableRow | {}) => {
                    const id = resolveRowId(row);
                    return typeof id === 'number' ? queries[projectPageIndex.get(id) ?? -1]?.isError ?? false : false;
                },
            };
        },
    });

    const rows: ProjectsTableRow[] = useMemo(
        () =>
            projects.map((project) => {
                const brand = brands.data.find((b) => b.id === project.brand_id) || null;
                const team = teams.data.find((t) => t.id === project.team_id) || null;
                const partnership = partnerships.data.find((p) => p.id === project.owned_by) || null;
                const projectUsers = users.data.filter((u) => project.users_assigned_to_campaigns.includes(u.id));
                const songStatsTimeSeries = project.song_id ? timeSeries.data.get(project.song_id) ?? null : null;

                return {
                    ...project,
                    brand,
                    team,
                    partnership,
                    users: projectUsers,
                    songStatsTimeSeries,
                };
            }),
        [projects, brands.data, teams.data, partnerships.data, users.data, timeSeries.data]
    );

    const meta: ProjectListMeta = {
        isLoading: projectsQuery.isLoading || projectsQuery.isFetchingNextPage,
        hasError: projectsQuery.status === 'error',
        getIsRowLoading: (row) => !('id' in row),
        isBrandLoading: brands.isLoadingFor,
        isTeamLoading: teams.isLoadingFor,
        isUsersLoading: users.isLoadingFor,
        isPartnershipLoading: partnerships.isLoadingFor,
        isTimeSeriesLoading: timeSeries.isLoadingFor,
        isTimeSeriesError: timeSeries.isErrorFor,
    };

    return {
        meta,
        rows,
        hasNextPage: projectsQuery.hasNextPage,
        fetchNextPage: projectsQuery.fetchNextPage,
        isFetchingNextPage: projectsQuery.isFetchingNextPage,
        count: projectsQuery.data?.pages[0]?.count,
    };
}
